import { useLocation } from '@tanstack/react-location';

/**
 * Hook to extract and validate property ID from URL parameters.
 *
 * Returns the property ID as a number if present and valid in the URL,
 * otherwise returns undefined.
 *
 * Works from anywhere within the Router context, including Layout components
 * that are outside of route elements.
 *
 * @example
 * ```tsx
 * const propertyId = usePropertyId();
 * if (!propertyId) {
 *   // Show property selection dialog
 * }
 * ```
 */
export const usePropertyId = (): number | undefined => {
    const location = useLocation();
    const pathname = location.current.pathname;

    // Extract property ID from pathname (format: /:propertyId/...)
    // Match the first segment after the leading slash
    const match = pathname.match(/^\/(\d+)/);

    if (!match) {
        return undefined;
    }

    const propertyId = Number(match[1]);

    // Validate that it's a valid positive number
    if (Number.isNaN(propertyId) || propertyId <= 0 || !Number.isInteger(propertyId)) {
        return undefined;
    }

    return propertyId;
};
